import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Users, Plus, Trash2, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { createGroupEvent, listGroupEvents, deleteGroupEvent, completeGroupAttendee } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const DISCIPLINES = [
  { id: 'jiujitsu', label: 'BJJ' },
  { id: 'self_defense', label: 'Self-Defense' },
  { id: 'personal_training', label: 'Personal Training' },
];

const blankEvent = () => ({
  title: '',
  description: '',
  category: 'jiujitsu',
  price_per_person: 40,
  capacity: 8,
  scheduled_date: '',
  scheduled_time: '',
  duration_minutes: 60,
  location: '',
});

const GroupEventsPanel = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(blankEvent());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user?.id) return;
    try {
      const res = await listGroupEvents(user.id);
      setRows(res.data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (Number(form.price_per_person) < 40) { toast.error('Minimum per-person price is $40'); return; }
    if (Number(form.capacity) < 2) { toast.error('Capacity must be at least 2'); return; }
    if (!form.scheduled_date || !form.scheduled_time) { toast.error('Pick a date and time'); return; }
    setBusy(true);
    try {
      await createGroupEvent({
        ...form,
        price_per_person: Number(form.price_per_person),
        capacity: Number(form.capacity),
        duration_minutes: Number(form.duration_minutes),
      });
      toast.success('Event created');
      setForm(blankEvent());
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create event');
    } finally { setBusy(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Cancel "${ev.title}"?`)) return;
    try { await deleteGroupEvent(ev.id); load(); }
    catch { toast.error('Cancel failed'); }
  };

  const markComplete = async (eventId, clientId, name) => {
    if (!window.confirm(`Mark ${name} as having completed this event? This awards XP + DG Points.`)) return;
    try {
      await completeGroupAttendee(eventId, clientId);
      toast.success(`${name} marked complete`);
      load();
    } catch { toast.error('Could not mark complete'); }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="group-events-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#C0A062]" />
          <h2 className="text-lg font-bold text-white">Group sessions & seminars</h2>
        </div>
        <Button size="sm" onClick={() => setShowNew((v) => !v)} className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="new-event-btn">
          <Plus className="w-4 h-4 mr-1" /> {showNew ? 'Cancel' : 'New event'}
        </Button>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Set a per-person price and a max capacity. Each attendee earns XP toward their belt when you mark them complete.</p>

      {showNew && (
        <form onSubmit={submit} className="space-y-3 bg-[#0d0d10] border border-zinc-800 rounded-md p-4 mb-5">
          <Input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="Event title" className="bg-[#09090B] border-zinc-700 text-white" data-testid="event-title" />
          <Textarea rows={2} value={form.description} onChange={(e) => setField('description', e.target.value)} placeholder="What\u2019s the event about?" className="bg-[#09090B] border-zinc-700 text-white" data-testid="event-description" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Discipline</Label>
              <select value={form.category} onChange={(e) => setField('category', e.target.value)} className="mt-1 w-full bg-[#09090B] border border-zinc-700 text-white rounded px-2 py-1.5 text-sm" data-testid="event-category">
                {DISCIPLINES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Price per person ($)</Label>
              <Input type="number" min="40" value={form.price_per_person} onChange={(e) => setField('price_per_person', e.target.value)} className="bg-[#09090B] border-zinc-700 text-white" data-testid="event-price" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Capacity</Label>
              <Input type="number" min="2" max="200" value={form.capacity} onChange={(e) => setField('capacity', e.target.value)} className="bg-[#09090B] border-zinc-700 text-white" data-testid="event-capacity" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Duration (min)</Label>
              <Input type="number" min="30" step="30" value={form.duration_minutes} onChange={(e) => setField('duration_minutes', e.target.value)} className="bg-[#09090B] border-zinc-700 text-white" data-testid="event-duration" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Date</Label>
              <Input type="date" value={form.scheduled_date} onChange={(e) => setField('scheduled_date', e.target.value)} className="bg-[#09090B] border-zinc-700 text-white" data-testid="event-date" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Time</Label>
              <Input type="time" value={form.scheduled_time} onChange={(e) => setField('scheduled_time', e.target.value)} className="bg-[#09090B] border-zinc-700 text-white" data-testid="event-time" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Location</Label>
              <Input value={form.location} onChange={(e) => setField('location', e.target.value)} placeholder="Address or venue" className="bg-[#09090B] border-zinc-700 text-white" data-testid="event-location" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="create-event-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create event
          </Button>
          <p className="text-[10px] text-zinc-600 text-center">Payment collection for group events is currently MOCKED — Stripe wiring deferred.</p>
        </form>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">No upcoming events.</p>
      ) : (
        <ul className="space-y-3" data-testid="event-list">
          {rows.map((ev) => (
            <li key={ev.id} className="bg-[#0d0d10] border border-zinc-800 rounded-md p-4" data-testid={`event-${ev.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold">{ev.title}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {ev.scheduled_date} @ {ev.scheduled_time} · {ev.category.replace('_', ' ')} · ${ev.price_per_person}/person · {ev.attendees?.length || 0} / {ev.capacity} booked
                    {ev.status !== 'open' && <span className="ml-2 text-amber-300">[{ev.status}]</span>}
                  </p>
                  {ev.location && <p className="text-xs text-zinc-500 mt-0.5">{ev.location}</p>}
                </div>
                <button onClick={() => remove(ev)} className="text-zinc-500 hover:text-red-400 p-1" aria-label="Delete" data-testid={`event-delete-${ev.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {(ev.attendees || []).length > 0 && (
                <ul className="mt-3 pt-3 border-t border-zinc-800 space-y-1">
                  {(ev.attendees || []).map((a) => (
                    <li key={a.client_id} className="flex items-center justify-between text-sm" data-testid={`attendee-${ev.id}-${a.client_id}`}>
                      <span className="text-zinc-300">{a.client_name}</span>
                      {a.completed ? (
                        <span className="text-emerald-300 text-xs flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                        </span>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-[#C0A062] hover:bg-[#C0A062]/10 h-7 px-2 text-xs" onClick={() => markComplete(ev.id, a.client_id, a.client_name)} data-testid={`attendee-complete-${ev.id}-${a.client_id}`}>
                          Mark complete
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GroupEventsPanel;
